import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { User, Buildings, Lightning } from "@phosphor-icons/react";

/**
 * Phase 281 — Calendar entry assignee chip with popover.
 *
 * Used inside each calendar bar (Dashboard Schedule Overview + /calendar page +
 * /jobs?view=month). Shows a 1–2 char initial; clicking opens a popover with
 * the full assignee name, type, and the entry's title + dates.
 *
 * Props:
 *   - name (string|""): "AG Plumbing & Heating" or "Jake Smith" or "" for unassigned
 *   - kind ("supplier"|"operative"|""): drives icon + color
 *   - title (string): the parent entry's title (job/task/emergency)
 *   - dateRange (string): e.g. "20 May → 22 May" or single date
 *   - type ("emergency"|"job"|"task"): for color theming
 *   - status (string): the entry's status to display
 */

function deriveInitials(name) {
  if (!name) return "—";
  const cleaned = name.trim().replace(/\s+/g, " ");
  if (!cleaned) return "—";
  const tokens = cleaned.split(" ").filter(Boolean);
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[1][0]).toUpperCase();
}

const TYPE_LABEL = { emergency: "Emergency", job: "Job", task: "Task" };

const CHIP_COLOR = {
  supplier: "bg-amber-500 text-white",
  operative: "bg-emerald-500 text-white",
  unassigned: "bg-slate-300 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
};

export default function AssigneeChip({ name, kind, title = "", dateRange = "", type = "job", status = "", testId = "" }) {
  const initials = deriveInitials(name);
  const colorKey = name ? (kind === "operative" ? "operative" : "supplier") : "unassigned";
  const Icon = type === "emergency" ? Lightning : kind === "operative" ? User : name ? Buildings : User;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className={`inline-flex items-center justify-center rounded-full h-4 w-4 sm:h-[18px] sm:w-[18px] text-[8px] sm:text-[9px] font-bold leading-none shrink-0 ring-1 ring-white/40 hover:ring-2 hover:ring-white transition ${CHIP_COLOR[colorKey]}`}
          title={name || "Unassigned — tap for details"}
          data-testid={testId || "assignee-chip"}
        >
          {initials}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={4}
        className="w-64 p-3 text-xs"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      >
        <div className="flex items-start gap-2 mb-2">
          <Icon size={20} weight="fill" className={type === "emergency" ? "text-red-500" : type === "task" ? "text-violet-500" : "text-blue-500"} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{TYPE_LABEL[type] || "Item"}</p>
            <p className="text-xs font-semibold leading-tight truncate" title={title}>{title || "(untitled)"}</p>
          </div>
        </div>
        <div className="space-y-1.5 border-t border-border pt-2">
          <div className="flex items-start gap-2">
            <span className={`inline-flex items-center justify-center rounded-full h-5 w-5 text-[9px] font-bold shrink-0 ${CHIP_COLOR[colorKey]}`}>
              {initials}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                {name ? (kind === "operative" ? "In-house operative" : "Supplier") : "Not yet assigned"}
              </p>
              <p className="text-xs font-medium truncate">{name || "—"}</p>
            </div>
          </div>
          {dateRange && (
            <p className="text-[11px] text-muted-foreground">📅 {dateRange}</p>
          )}
          {status && (
            <p className="text-[11px] text-muted-foreground capitalize">⌛ {status.replace(/_/g, " ")}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { deriveInitials };
